// File: components/StandaloneFileUpload.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import StyledText from "./StyledText";
import { CorporateTheme, CreateFormTheme, font, Theme } from "../../utils/themes";
import CSVIcon from "../../assets/icons/CSVIcon";
import { formatFileSize } from "../../utils/helper";
import StyledInput from "./StyledInput";
import CrossIcon from "../../assets/icons/CrossIcon";
import { useTheme } from "../../middleware/ThemeContext";
import { useToast } from "../toast/ToastContext";
import RequiredIcon from "../../assets/icons/RequiredIcon";

interface UploadedFile {
    file: File;
    name: string;
    type: string;
    rename: string;
    progress: number;
}


interface StandaloneFileUploadProps {
    multiple?: boolean;
    accept?: string;
    onChange?: (files: UploadedFile[]) => void;
    showRenameInput?: boolean;  // <-- New optional prop
    icon?: React.ReactNode;
    removeFile: (state: boolean) => void;

}

const allowedTypes = [
    "text/csv",
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
];

const allowedExtensions = [".xlsx"];

// Common file validator
const isValidFile = (file: File) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    return allowedTypes.includes(file.type) || allowedExtensions.includes(ext);
};

const FileUpload: React.FC<StandaloneFileUploadProps> = ({
    multiple = false,
    accept = "*/*",
    onChange,
    showRenameInput = false,  // default false
    icon,
    removeFile
}) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [progress, setProgress] = useState<Record<string, number>>({});
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { theme } = useTheme();
    const showToast = useToast();
    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setDragging(false);
            // const droppedFiles = Array.from(event.dataTransfer.files).filter(file =>
            //     file.type.match(accept.replace("*", ".*"))
            // );
            const droppedFiles = Array.from(event.dataTransfer.files).filter(isValidFile);
            updateFiles(droppedFiles);
        },
        // [accept]
        []
    );

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!dragging) setDragging(true);
    };

    const handleDragLeave = () => {
        setDragging(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        // const selectedFiles = Array.from(e.target.files).filter(file =>
        //     file.type.match(accept.replace("*", ".*"))
        // );
        const selectedFiles = Array.from(e.target.files).filter(isValidFile);
        updateFiles(selectedFiles);
        if (selectedFiles.length === 0) {
            showToast("Upload Failed", "Only CSV or Excel files are allowed.", "error");
            e.target.value = "";
        }
    };

    const updateFiles = (newFiles: File[]) => {
        const uploadedFiles: UploadedFile[] = newFiles.map((file) => {
    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");

            return {
                file,
                name: file.name,
                type: file.type,
                rename: nameWithoutExt,
                progress: 0,
            }
        });

        const updatedFiles = multiple ? [...files, ...uploadedFiles] : uploadedFiles.slice(0, 1);
        setFiles(updatedFiles);
        onChange?.(updatedFiles);
    };

    const handleRenameChange = (index: number, value: string) => {
        const updatedFiles = [...files];
        updatedFiles[index].rename = value;
        setFiles(updatedFiles);
    };

    useEffect(() => {
        const handleProgress = (event: any) => {
            const { name, progress } = event.detail;
            setProgress(prev => ({
                ...prev,
                [name]: progress
            }));
        };

        window.addEventListener('file-upload-progress', handleProgress);

        return () => {
            window.removeEventListener('file-upload-progress', handleProgress);
        };
    }, []);

    const handleRemoveFile = (indexToRemove: number) => {
        const updatedFiles = files.filter((_, index) => index !== indexToRemove);
        setFiles(updatedFiles);
        removeFile(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    return (
        <>
            <DropZone
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                tabIndex={0}
                role="button"
                aria-label="File Upload Dropzone"
                theme={theme}
                dragging={dragging}
            >
                {/* {dragging && <DropOverlay>Drop Here</DropOverlay>} */}
                <div>{icon}</div>
                <StyledText
                    textColor={theme.CorporateTheme.colors.textSecondary}
                    fontSize="16px"
                    fontWeight={500}
                    className="mt-2"
                >
                    Drag & Drop file here or&nbsp;
                    <strong className="text-decoration-underline">Click to upload</strong>
                </StyledText>
                <StyledText
                    textColor={theme.CorporateTheme.colors.textSecondary}
                    fontSize="14px"
                    fontWeight={400}
                    className="mt-2"
                    lineHeight="20px"
                >
                    Upload a Excel file containing your data. The system will
                    <br />auto-map columns where possible.
                </StyledText>
                <HiddenInput
                    ref={fileInputRef}
                    type="file"
                    multiple={multiple}
                    // accept={accept}
                    accept=".csv, .xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleInputChange}
                />
            </DropZone>

            {files.length > 0 ? (
                <FileList theme={theme}>
                    {files.map((file, index) => {
                        const fileProgress = progress[file.file.name] || 0;

                        return (
                            <FileItem key={file.file.name}>
                                <div className="d-flex align-items-center gap-3">
                                    <div>
                                        <CSVIcon />
                                    </div>
                                    <div className="d-flex flex-column w-100 ">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <StyledText fontWeight={600} fontSize="16px" className="mt-1">
                                                {file.file.name}
                                            </StyledText>
                                            {fileProgress === 100 ? (
                                                <div
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => handleRemoveFile(index)}
                                                >
                                                    <CrossIcon
                                                        size={16}
                                                        color={theme.CorporateTheme.colors.textSecondary}
                                                    />
                                                </div>
                                            ) : null}
                                        </div>
                                        <StyledText
                                            fontWeight={400}
                                            fontSize="14px"
                                            textColor={theme.CorporateTheme.colors.textSecondary}
                                            className="mt-1"
                                        >
                                            {formatFileSize(file.file.size)}
                                        </StyledText>
                                        {fileProgress < 100 ? (
                                            <div className="d-flex gap-1 align-items-center">
                                                <ProgressBar className="mt-2 w-100">
                                                    <Progress width={progress[file.file.name] || 0} />
                                                </ProgressBar>
                                                <StyledText
                                                    fontWeight={600}
                                                    fontSize="14px"
                                                    textColor={theme.CorporateTheme.colors.textSecondary}
                                                    fontFamily={font.lato}
                                                >
                                                    {fileProgress}%
                                                </StyledText>
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {showRenameInput && fileProgress === 100 && (
                                    <div className="d-flex flex-column mt-3 gap-2">
                                        <StyledText
                                            fontWeight={600}
                                            fontSize="14px"
                                            textColor={theme.CorporateTheme.colors.headerColor}
                                        >
                                            Import Name <RequiredIcon />
                                        </StyledText>
                                        <StyledInput
                                            type="text"
                                            theme={theme}
                                            placeholder="Give name to your import"
                                            value={file.rename}
                                            $phFontWeight={"500"}
                                            $fontWeight={"500"}
                                            color={theme.CreateFormTheme.colors.placeHolder}
                                            onChange={(e) => handleRenameChange(index, e.target.value)}
                                        />
                                        <StyledText
                                            fontWeight={500}
                                            fontSize="13px"
                                            textColor={theme.CorporateTheme.colors.textSecondary}
                                        >
                                            Please give a unique name for this import <RequiredIcon />
                                        </StyledText>
                                    </div>
                                )}
                            </FileItem>
                        );
                    })}
                </FileList>
            ) : null}
        </>
    );
};

export default FileUpload;

// Styled Components

const DropZone = styled.div<{ theme: Theme; dragging: boolean }>`
//   border: 1px dashed ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
border:${({ theme, dragging }) =>
        dragging ? `2px dashed ${theme.CorporateTheme.colors.primary}` : ` 1px dashed ${theme.CreateFormTheme.colors.inputBorders}`};
  border-radius: 6px;
  padding: 30px;
  text-align: center;
  color: #666;
  cursor: pointer;
  position: relative;
  user-select: none;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.bannerBg};
  &:focus {
    outline: 2px solid #007bff;
  }
`;

const DropOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 123, 255, 0.15);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #007bff;
  font-weight: 600;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  pointer-events: none;
`;

const HiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const FileList = styled.div<{ theme: Theme }>`
  margin-top: 15px;
  border: 1px dashed ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
    border-radius: 6px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.bannerBg};
    padding: 25px;
`;

const FileItem = styled.div`
  margin-bottom: 10px;
`;

const FileName = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
`;

const Progress = styled.div<{ width: number }>`
  height: 100%;
  width: ${(props) => props.width}%;
  background-color: ${CorporateTheme.colors.blueberry};
  transition: width 0.3s ease;
`;

const RenameInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${CreateFormTheme.colors.inputBorders};
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
`;

